import { PageTitle } from '../../_metronic/layout/core'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {HomeworkSettingsForm, homeworkUpdatePayload} from './HomeworkSettingsForm'
import {EDUCATION_REQUEST_URL} from '../../constants/environments'
import './Detail.scss'

import {
  callbackProps,
  DetailQueryState,
  homeworkInitial,
  ID,
  IHomework,
  IHomeworkDetailReport,
  IHomeworkVoiceRecord,
  IStory,
  IStoryQuestionGroup,
  LANGUAGE_OPTIONS,
  reduxProps,
  storyInitialValues,
  storyQuestionGroupInitialValues,
} from '../../constants'
import {
  actionDeleteHomework,
  actionGetHomeworkById,
  actionGetStoryMeta,
  actionHomeworkReport,
  actionUpdateHomework,
} from '../../redux/education'
import {
  actionDeleteModalOpen,
  actionQuestionDetailModalOpen,
  actionStoryReadingModalOpen,
  actionStudentDetailModalOpen,
} from '../../redux/modal'
import { toast } from 'react-toastify'
import {HomeworkStudentReports} from './HomeworkStudentReports'
import {useHomeworkQuiz} from './useHomeworkQuiz'
import { IStoryMeta } from '../../constants/types/education/model/story-meta.model'
import { actionGetStoryDetail } from "../../redux/education/actions/story.action";
import { actionGetStoryQuestionGroup } from "../../redux/question";
import { getHomeworkVoiceRecords } from '../../services/education/homework.service'

function ActivitySuggestion({text}: {text: string}) {
  const [expanded, setExpanded] = useState(false)
  const isLong = text.length > 50
  const preview = text.slice(0, 100).replace(/\s+\S*$/, '').trimEnd()

  return (
    <div className='value homework-activity'>
      <span id='homework-activity-text'>{isLong && !expanded ? `${preview}…` : text}</span>
      {isLong && (
        <button type='button' className='homework-activity-toggle'
          aria-expanded={expanded} aria-controls='homework-activity-text'
          onClick={() => setExpanded(value => !value)}>
          {expanded ? 'Daha az göster' : 'Devamını göster'}
        </button>
      )}
    </div>
  )
}

export function HomeworkDetail() {
  const navigate = useNavigate()
  const loadGeneration = useRef(0)
  const [reportsLoading, setReportsLoading] = useState(false)
  const [reportsError, setReportsError] = useState(false)
  const [reportsRefresh, setReportsRefresh] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [reloadVersion, setReloadVersion] = useState(0)
  const [storyLoading, setStoryLoading] = useState(true)
  const [storyError, setStoryError] = useState(false)
  const [homework, setHomework] = useState<IHomework>(homeworkInitial)
  const [classHomeWorkReport, setClassHomeWorkReport] = useState<IHomeworkDetailReport[]>([])
  const [voiceRecords, setVoiceRecords] = useState<IHomeworkVoiceRecord[]>([])
  const [voiceRecordsLoading, setVoiceRecordsLoading] = useState(false)
  const [voiceRecordsError, setVoiceRecordsError] = useState(false)
  const [voiceRecordsRefresh, setVoiceRecordsRefresh] = useState(0)
  const [story, setStory] = useState<IStory>(storyInitialValues)
  const { categories, classroom, students } = useSelector(
    (state: reduxProps) => state.educationReducer
  )

  const [loadReport, setLoadReport] = useState<boolean>(false)
  const [metaDataLoaded, setMetaDataLoaded] = useState<boolean>(false)
  const [questionLoaded, setQuestionLoaded] = useState<boolean>(false)

  const [storyMeta, setStoryMeta] = useState<IStoryMeta>({
    activity: '',
    gradeLevels: [],
    pypThemes: [],
    terms: [],
  })

  const [questionGroup, setQuestionGroup] = useState<IStoryQuestionGroup>(storyQuestionGroupInitialValues)
  let { id } = useParams<DetailQueryState>()

  useEffect(() => {
    let active = true
    loadGeneration.current++
    setLoading(true)
    setLoadError(false)
    setStoryLoading(true)
    setStoryError(false)
    setMetaDataLoaded(false)
    setQuestionLoaded(false)
    setLoadReport(false)
    setClassHomeWorkReport([])
    setStoryMeta({activity: '', gradeLevels: [], pypThemes: [], terms: []})
    setQuestionGroup(storyQuestionGroupInitialValues)
    dispatch(
      actionGetHomeworkById(id, (r) => {
        if (!active) return
        setLoading(false)
        if (r.type === 'SUCCESS') {
          const homeworkData = homeworkUpdatePayload(r.payload.data)
          const languageCode = homeworkData.languageCode || 'tr-TR'
          let storyId = homeworkData.storyId
          dispatch(
            actionGetStoryDetail({ storyId: storyId, languageCode }, (r) => {
              if (!active) return
              setStoryLoading(false)
              if (r.type !== 'SUCCESS') {
                setStoryError(true)
                setMetaDataLoaded(true)
                setQuestionLoaded(true)
                return
              }
              let storyItem: IStory = r.payload.data
              storyItem.categories = categories
                .filter((categoryItem) => storyItem.categoryIds.includes(categoryItem.id))
                .map((item) => item.defaultTitle)
                .join(',')

              dispatch(
                actionGetStoryMeta(
                  {
                    storyId: storyId,
                    languageCode,
                  },
                  (r) => {
                    if (!active) return
                    if (r.type === 'SUCCESS') {
                      setStoryMeta(r.payload.data)
                    }
                    setMetaDataLoaded(true)
                  }
                )
              )

              dispatch(
                actionGetStoryQuestionGroup(
                  {
                    languageCode,
                    storyId: storyId,
                  },
                  (r) => {
                    if (!active) return
                    if (r.type === 'SUCCESS') {
                      setQuestionGroup(r.payload.data)
                    } else {
                      setQuestionGroup(storyQuestionGroupInitialValues)
                    }
                    setQuestionLoaded(true)
                  }
                )
              )
              setStory(storyItem)
            })
          )
          setHomework(homeworkData)
        } else {
          setLoadError(true)
        }
      })
    )
    return () => { active = false; loadGeneration.current++ }
  }, [id, reloadVersion])

  const dispatch = useDispatch()
  const quiz = useHomeworkQuiz(homework.storyId, homework.languageCode || 'tr-TR', classroom.id,
    !loading && !loadError && String(homework.id) === String(id))

  useEffect(() => {
    if (!loadReport || loading || loadError || !homework.id) return
    loadReports(homework.id, classroom.id)
  }, [loadReport, loading, loadError, homework.id, classroom.id, students, reportsRefresh])

  useEffect(() => {
    if (!metaDataLoaded) return
    if (!questionLoaded) return
    setLoadReport(true)
  }, [metaDataLoaded, questionLoaded])

  useEffect(() => {
    let active = true
    setVoiceRecords([])
    setVoiceRecordsError(false)
    if (!id) return
    setVoiceRecordsLoading(true)
    getHomeworkVoiceRecords(id)
      .then(records => { if (active) setVoiceRecords(records) })
      .catch(() => { if (active) setVoiceRecordsError(true) })
      .finally(() => { if (active) setVoiceRecordsLoading(false) })
    return () => { active = false }
  }, [id, voiceRecordsRefresh])

  function loadReports(homeworkId: ID, classroomId: ID) {
    if (!classroomId || classroomId === 0) return
    const generation = loadGeneration.current
    setReportsLoading(true)
    setReportsError(false)
    setClassHomeWorkReport([])
    const userIds = students.map(item => item.id)
    dispatch(actionHomeworkReport(
      {classroomId, homeworkId, users: JSON.stringify(userIds)},
      r => {
        if (generation !== loadGeneration.current) return
        setReportsLoading(false)
        if (r.type !== 'SUCCESS') {setReportsError(true); return}
        setClassHomeWorkReport(r.payload.data)
      }
    ))
  }

  function openUserDetailModal(userId: ID) {
    dispatch(
      actionStudentDetailModalOpen({
        UserId: userId,
      })
    )
  }

  function openStoryReadingModal() {
    const languageCode = homework.languageCode || story.languageCode || 'tr-TR'
    dispatch(
      actionStoryReadingModalOpen({
        StoryId: story.storyId,
        LanguageCode: languageCode,
        StoryDetailId: story.storyDetailId,
      })
    )
  }

  function openStoryQuestions() {
    const languageCode = homework.languageCode || story.languageCode || 'tr-TR'
    dispatch(
      actionQuestionDetailModalOpen({
        StoryId: story.storyId,
        LanguageCode: languageCode,
        ShowType: 'HomeworkDetail',
      })
    )
  }

  const saveChanges = (draft: IHomework): Promise<IHomework> => new Promise((resolve, reject) => {
    const generation = loadGeneration.current
    const update = () => {
      if (generation !== loadGeneration.current) { reject(new Error('Sorumluluk değişti. Lütfen tekrar deneyin.')); return }
      dispatch(actionUpdateHomework(homeworkUpdatePayload(draft), (r) => {
        if (generation !== loadGeneration.current) { reject(new Error('Sorumluluk değişti.')); return }
        if (r.type === 'SUCCESS' && r.payload?.data) {
          const saved = homeworkUpdatePayload(r.payload.data)
          setHomework(saved)
          toast.success('Sorumluluk ayarları güncellendi')
          resolve(saved)
          setReloadVersion(value => value + 1)
        } else {
          reject(new Error('Değişiklikler kaydedilemedi. Lütfen tekrar deneyin.'))
        }
      }))
    }
    if (draft.languageCode !== homework.languageCode) {
      dispatch(actionGetStoryDetail({storyId: draft.storyId, languageCode: draft.languageCode}, r => {
        if (r.type === 'SUCCESS' && r.payload?.data?.storyDetailId) update()
        else reject(new Error('Bu kitabın seçilen dilde içeriği bulunamadı. Lütfen başka bir dil seçin.'))
      }))
    } else update()
  })
  const redirectPage = (a: callbackProps) => {
    if (a.type === 'SUCCESS') {
      navigate('/homework/')
    }
  }

  function deleteHomeworkHandle() {
    dispatch(actionDeleteHomework(id, redirectPage))
  }

  function deleteHomework() {
    dispatch(
      actionDeleteModalOpen({
        deleteModalHandle: deleteHomeworkHandle,
        title: story.title,
        buttonText: 'Kaldır',
        contentId: id,
        content: 'Bu sorumluluğu kaldırmak istediğinize emin misiniz?',
      })
    )
  }

  return (
    <>
      <PageTitle description={'Sorumluluk ekleyip varolan sorumlulukları düzenleyebilirsiniz.'}>
        Sorumluluk Düzenle
      </PageTitle>
      <div id={'homework-detail'}>
        {loading ? <div className='card homework-detail-message' role='status'>Sorumluluk yükleniyor…</div>
          : loadError ? <div className='card homework-detail-message' role='alert'>
            <p>Sorumluluk bilgileri yüklenemedi.</p>
            <button type='button' className='btn btn-light' onClick={() => setReloadVersion(value => value + 1)}>Tekrar dene</button>
          </div> : <>
        <div className='homework-detail-grid'>
          <HomeworkSettingsForm key={String(id)} homework={homework} onSave={saveChanges} onDelete={deleteHomework} />
          <section className='homework-book-panel' aria-label='Kitap bilgileri'>
            {storyLoading ? <p role='status'>Kitap bilgileri yükleniyor…</p> : storyError ?
              <div role='alert'><p>Kitap bilgileri yüklenemedi.</p><button type='button' className='btn btn-light' onClick={() => setReloadVersion(value => value + 1)}>Tekrar dene</button></div> : <>
            <div className='homework-book-overview'>
              <img alt={story.title} className='homework-book-cover'
                src={`${EDUCATION_REQUEST_URL}/story/content/${story.tabletCover}`} />
              <div>
                <span className='homework-book-eyebrow'>SORUMLULUK KİTABI</span>
                <h2>{story.title}</h2>
                <span className='homework-language-badge'>{LANGUAGE_OPTIONS.find(item => item.value === homework.languageCode)?.label || homework.languageCode}</span>
                <div className='homework-book-actions'>
                  <button type='button' className='btn btn-light-primary' onClick={openStoryReadingModal}>
                    <i className='fa-solid fa-book-open' aria-hidden='true' /> Hikayeyi oku
                  </button>
                  {questionGroup.questionCount > 0 && <button type='button' className='btn btn-light' onClick={openStoryQuestions}>
                    <i className='fa-regular fa-pen-to-square' aria-hidden='true' /> Cevap anahtarı
                  </button>}
                </div>
              </div>
            </div>
          <div className='homework-book-description'>
            <div>
              <span className={'description mt-2'}>{story.description}</span>
              <div className={'detail'}>
                <div className={'line mt-3'}>
                  <span className={'title'}>Dil:</span>
                  <span className={'value'}>
                    {LANGUAGE_OPTIONS.find((item) => item.value === homework.languageCode)?.label ||
                      homework.languageCode ||
                      'Türkçe'}
                  </span>
                </div>
                {storyMeta.terms.length > 0 && (
                  <div className={'line mt-3'}>
                    <span className={'title'}>Kavramlar:</span>
                    <span className={'value'}>{storyMeta.terms.join(',')}</span>
                  </div>
                )}

                {storyMeta.gradeLevels.length > 0 && (
                  <div className={'line mt-3'}>
                    <span className={'title'}>Okul Yılı:</span>
                    <span className={'value'}>{storyMeta.gradeLevels.join(',')}</span>
                  </div>
                )}
                <div className={'line mt-3'}>
                  <span className={'title'}>Kategorisi:</span>
                  <span className={'value'}>{story.categories}</span>
                </div>
                {storyMeta.pypThemes.length > 0 && (
                  <div className={'line mt-3'}>
                    <span className={'title'}>PYP Teması:</span>
                    <span className={'value'}>{storyMeta.pypThemes.join(',')}</span>
                  </div>
                )}
                {storyMeta.activity && (
                  <div className={'line mt-3'}>
                    <span className={'title'}>Etkinlik/Proje önerisi:</span>
                    <ActivitySuggestion key={`${id}-${storyMeta.activity}`} text={storyMeta.activity} />
                  </div>
                )}
              </div>
            </div>
          </div>
            </>}
          </section>
        </div>
        <HomeworkStudentReports key={`${id}-${classroom.id}`} reports={classHomeWorkReport} students={students}
          loading={reportsLoading || !loadReport} error={reportsError} quiz={quiz} storyTitle={story.title}
          onRetry={() => setReportsRefresh(value => value + 1)} onOpenStudent={openUserDetailModal}
          voiceRecords={voiceRecords} voiceLoading={voiceRecordsLoading} voiceError={voiceRecordsError}
          onRefreshVoice={() => setVoiceRecordsRefresh(value => value + 1)} requestVoiceRecording={homework.requestVoiceRecording}/>
        </>}
      </div>
    </>
  )
}
